import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { PointMm } from '~/domain/geometry/patientGeometry';
import {
    SummaryStatistics,
    summaryStatistics,
} from '~/domain/statistics/summaryStatistics';
import type { Grid } from '~/engine/data/common/Grid';
import type { ImageObject } from './ImageObject';

/**
 * Half the thickness of the region the circle covers. The circle is a disc one
 * voxel thick along its normal, and the voxel size comes from the image, not
 * from the view showing it.
 */
function halfThicknessMm(grid: Grid, normal: ReadonlyVec3): number {
    return grid.voxelExtentAlong(normal) / 2;
}

/**
 * The box of voxel indices that could have their centre inside the region,
 * clipped to the volume. A sphere of radius `reachMm` around the centre
 * contains the disc however it is tilted, and a sphere looks the same from
 * every axis, so along each grid axis the box extends `reachMm` divided by
 * that axis' voxel spacing.
 */
function candidateBox(
    grid: Grid,
    centerMm: PointMm,
    reachMm: number
): { lo: [number, number, number]; hi: [number, number, number] } {
    const size = grid.getSize();
    // toVoxelSpace gives continuous coordinates, where voxel i covers
    // [i, i+1] and its centre is at i + 0.5. Subtracting the half puts the
    // centre on whole-number index coordinates, so the rounding below
    // compares like with like; without it the box sits half a voxel off and
    // misses a row on one side.
    const centre = vec3.subtract(
        vec3.create(),
        grid.toVoxelSpace(centerMm),
        vec3.fromValues(0.5, 0.5, 0.5)
    );
    const spacings = [grid.resX, grid.resY, grid.resZ];
    const lo: [number, number, number] = [0, 0, 0];
    const hi: [number, number, number] = [0, 0, 0];
    for (let axis = 0; axis < 3; axis++) {
        const half = reachMm / spacings[axis];
        lo[axis] = Math.max(0, Math.ceil(centre[axis] - half));
        hi[axis] = Math.min(size[axis] - 1, Math.floor(centre[axis] + half));
    }
    return { lo, hi };
}

/**
 * Yields the value of every voxel inside the region, each once, in a fixed
 * order (z, then y, then x) so the result never varies between runs. The box
 * is clipped to the volume, so a null read means the image has no value for
 * that voxel, not that the voxel is outside the image; it is yielded as NaN,
 * which {@link summaryStatistics} leaves out.
 */
function* enclosedVoxelValues(
    image: ImageObject,
    centerMm: PointMm,
    radiusMm: number,
    normal: ReadonlyVec3
): Generator<number> {
    const grid = image.grid;
    const halfDepthMm = halfThicknessMm(grid, normal);
    const { lo, hi } = candidateBox(
        grid,
        centerMm,
        Math.hypot(radiusMm, halfDepthMm)
    );
    const radiusSq = radiusMm * radiusMm;
    const voxel = vec3.create();
    const offset = vec3.create();
    for (let z = lo[2]; z <= hi[2]; z++) {
        for (let y = lo[1]; y <= hi[1]; y++) {
            for (let x = lo[0]; x <= hi[0]; x++) {
                vec3.set(voxel, x, y, z);
                const world = grid.voxelCenterToWorldSpace(voxel);
                vec3.subtract(offset, world, centerMm);
                const alongNormal = vec3.dot(offset, normal);
                if (Math.abs(alongNormal) > halfDepthMm) {
                    continue;
                }
                const inPlaneSq =
                    vec3.squaredLength(offset) - alongNormal * alongNormal;
                if (inPlaneSq > radiusSq) {
                    continue;
                }
                yield image.getImageObjectValueByCoordinate(world) ??
                    Number.NaN;
            }
        }
    }
}

/**
 * Intensity statistics (Min, max, mean and SD) of the voxels inside a circle of radius `radiusMm`
 * centred at `centerMm`, lying in the plane with normal `normalDirection`
 * (SR-WOR-H-17-5).
 *
 * A voxel is inside when its centre is, and each one is read once. The
 * figures therefore depend on the circle and the image only: turning the view
 * does not change them, and a circle tilted against the grid reads the voxels
 * it actually covers.
 *
 * Values are read the same way the cursor readout reads them, so the two
 * always agree.
 *
 * @returns null when the radius or the normal is degenerate, or when no voxel
 *   under the circle has a finite value.
 * @throws when the image is still loading. A slice that has not arrived reads
 *   as no voxel, so figures over the slices present would look like the
 *   circle's. Callers refuse the circle before reading (SR-WOR-H-37-3).
 */
export function circleIntensityStatistics(
    image: ImageObject,
    centerMm: PointMm,
    radiusMm: number,
    normalDirection: ReadonlyVec3
): SummaryStatistics | null {
    if (!image.parent.isComplete) {
        throw new Error(
            'circleIntensityStatistics: the image is not whole; refuse the circle before reading it'
        );
    }
    // Negated so a NaN radius or normal is refused too.
    if (!(radiusMm > 0) || !(vec3.length(normalDirection) > 0)) {
        return null;
    }
    const normal = vec3.normalize(vec3.create(), normalDirection);
    const enclosed = summaryStatistics(
        enclosedVoxelValues(image, centerMm, radiusMm, normal)
    );
    if (enclosed) {
        return enclosed;
    }
    // A circle smaller than a voxel encloses no voxel centre. The voxel it
    // sits inside is the only honest answer, and reporting it keeps a tiny
    // circle from reading as "no data" over perfectly good image.
    const atCentre = image.getImageObjectValueByCoordinate(centerMm);
    return atCentre === null || !Number.isFinite(atCentre)
        ? null
        : summaryStatistics([atCentre]);
}
